import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Form,
  Pagination,
  Select,
  Table,
  Popup,
  Icon,
} from 'semantic-ui-react';
import {
  API,
  copy,
  isAdmin,
  showError,
  showSuccess,
  showWarning,
  timestamp2string,
} from '../helpers';
import { useTranslation } from 'react-i18next';

import { ITEMS_PER_PAGE } from '../constants';
import { renderColorLabel, renderQuota } from '../helpers/render';

/**
 * 截断长文本，超过指定长度则省略
 * @param {string} text - 原始文本
 * @param {number} max - 最大长度
 * @returns {string} 截断后文本
 */
function truncate(text, max = 32) {
  if (!text) return '-';
  const s = String(text);
  return s.length > max ? s.slice(0, max) + '...' : s;
}

/**
 * 渲染时间戳，点击可复制请求ID
 * @param {number} timestamp - 时间戳
 * @param {string} request_id - 请求ID
 * @returns {JSX.Element}
 */
function renderTimestamp(timestamp, request_id) {
  return (
    <Popup
      content={`点击复制请求 ID：${request_id}`}
      trigger={
        <code
          onClick={async () => {
            try {
              if (await copy(request_id)) {
                showSuccess(`已复制请求 ID：${request_id}`);
              } else {
                showWarning(`请求 ID 复制失败：${request_id}`);
              }
            } catch (err) {
              showWarning('复制操作失败');
            }
          }}
          style={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: 11 }}
        >
          {timestamp2string(timestamp)}
        </code>
      }
    />
  );
}

/**
 * 渲染日志类型标签
 * @param {number} type - 日志类型
 * @returns {JSX.Element}
 */
function renderType(type) {
  const cfg = {
    1: { text: '充值', bg: '#f0fdf4', color: '#16a34a', border: 'rgba(22,163,74,0.3)' },
    2: { text: '消费', bg: 'rgba(0,0,0,0.08)', color: '#18181b', border: 'rgba(0,0,0,0.16)' },
    3: { text: '管理', bg: '#fffbeb', color: '#ea580c', border: 'rgba(217,119,6,0.3)' },
    4: { text: '系统', bg: '#faf5ff', color: '#9333ea', border: 'rgba(147,51,234,0.3)' },
    5: { text: '测试', bg: '#fdf2f8', color: '#db2777', border: 'rgba(219,39,119,0.3)' },
  }[type] || { text: '未知', bg: 'rgba(0,0,0,0.03)', color: '#6b7280', border: 'rgba(0,0,0,0.12)' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 8px',
      borderRadius: 6,
      background: cfg.bg,
      color: cfg.color,
      fontSize: 12,
      fontWeight: 500,
      border: `1px solid ${cfg.border}`,
    }}>
      {cfg.text}
    </span>
  );
}

/**
 * 渲染日志详情列：
 * - 内容过长时默认截断，鼠标悬停显示完整
 * - 附加耗时/流式/系统提示重置等徽标
 * @param {Object} log - 日志对象
 * @returns {JSX.Element}
 */
function renderDetail(log) {
  const content = log.content || '';
  const tooLong = content.length > 32;
  const display = tooLong ? truncate(content, 32) : content;

  return (
    <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 6, alignItems: 'center' }}>
      {tooLong ? (
        <Popup
          content={
            <div style={{ maxWidth: 480, maxHeight: 240, overflow: 'auto', wordBreak: 'break-word' }}>
              {content}
            </div>
          }
          hoverable
          wide
          trigger={
            <span style={{
              cursor: 'help',
              borderBottom: '1px dotted #94a3b8',
              color: '#374151',
              fontSize: 12,
              maxWidth: 160,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {display}
            </span>
          }
        />
      ) : (
        <span style={{ wordBreak: 'break-word', color: '#374151', fontSize: 12 }}>{display}</span>
      )}
      {log.elapsed_time > 0 && (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          padding: '2px 6px',
          borderRadius: 4,
          background: 'rgba(0,0,0,0.03)',
          color: '#6b7280',
          fontSize: 11,
          fontWeight: 500,
          whiteSpace: 'nowrap',
        }}>
          <Icon name='clock outline' style={{ margin: 0, fontSize: 10 }} /> {log.elapsed_time} ms
        </span>
      )}
      {log.is_stream && (
        <span style={{
          display: 'inline-block',
          padding: '2px 6px',
          borderRadius: 4,
          background: '#fdf2f8',
          color: '#db2777',
          fontSize: 11,
          fontWeight: 500,
        }}>Stream</span>
      )}
      {log.system_prompt_reset && (
        <span style={{
          display: 'inline-block',
          padding: '2px 6px',
          borderRadius: 4,
          background: '#fef2f2',
          color: '#dc2626',
          fontSize: 11,
          fontWeight: 500,
        }}>SysReset</span>
      )}
    </div>
  );
}

const LogsTable = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [showStat, setShowStat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [logType, setLogType] = useState(0);
  const [hoveredRow, setHoveredRow] = useState(null);
  const isAdminUser = isAdmin();
  const now = new Date();
  const [inputs, setInputs] = useState({
    username: '',
    token_name: '',
    model_name: '',
    start_timestamp: timestamp2string(0),
    end_timestamp: timestamp2string(now.getTime() / 1000 + 3600),
    channel: '',
  });
  const {
    username,
    token_name,
    model_name,
    start_timestamp,
    end_timestamp,
    channel,
  } = inputs;

  const [stat, setStat] = useState({ quota: 0, token: 0 });

  const LOG_OPTIONS = [
    { key: '0', text: t('log.type.all'), value: 0 },
    { key: '1', text: t('log.type.topup'), value: 1 },
    { key: '2', text: t('log.type.usage'), value: 2 },
    { key: '3', text: t('log.type.admin'), value: 3 },
    { key: '4', text: t('log.type.system'), value: 4 },
    { key: '5', text: t('log.type.test'), value: 5 },
  ];

  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  /**
   * 获取个人统计信息
   */
  const getLogSelfStat = useCallback(async () => {
    try {
      const localStartTimestamp = Date.parse(start_timestamp) / 1000;
      const localEndTimestamp = Date.parse(end_timestamp) / 1000;
      if (isNaN(localStartTimestamp) || isNaN(localEndTimestamp)) {
        showError('时间格式错误');
        return;
      }
      const res = await API.get(
        `/api/log/self/stat?type=${logType}&token_name=${token_name}&model_name=${model_name}&start_timestamp=${localStartTimestamp}&end_timestamp=${localEndTimestamp}`
      );
      const { success, message, data } = res.data;
      if (success) {
        setStat(data);
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '获取统计信息失败');
    }
  }, [logType, token_name, model_name, start_timestamp, end_timestamp]);

  /**
   * 获取管理员统计信息
   */
  const getLogStat = useCallback(async () => {
    try {
      const localStartTimestamp = Date.parse(start_timestamp) / 1000;
      const localEndTimestamp = Date.parse(end_timestamp) / 1000;
      if (isNaN(localStartTimestamp) || isNaN(localEndTimestamp)) {
        showError('时间格式错误');
        return;
      }
      const res = await API.get(
        `/api/log/stat?type=${logType}&username=${username}&token_name=${token_name}&model_name=${model_name}&start_timestamp=${localStartTimestamp}&end_timestamp=${localEndTimestamp}&channel=${channel}`
      );
      const { success, message, data } = res.data;
      if (success) {
        setStat(data);
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '获取统计信息失败');
    }
  }, [logType, username, token_name, model_name, start_timestamp, end_timestamp, channel]);

  /**
   * 点击眼睛图标切换统计显示
   */
  const handleEyeClick = useCallback(async () => {
    if (!showStat) {
      if (isAdminUser) {
        await getLogStat();
      } else {
        await getLogSelfStat();
      }
    }
    setShowStat(!showStat);
  }, [showStat, isAdminUser, getLogStat, getLogSelfStat]);

  /**
   * 是否显示用户Token额度列
   */
  const showUserTokenQuota = useCallback(() => logType !== 5, [logType]);

  /**
   * 加载日志数据
   * @param {number} startIdx - 起始页码
   */
  const loadLogs = useCallback(async (startIdx) => {
    try {
      let url = '';
      const localStartTimestamp = Date.parse(start_timestamp) / 1000;
      const localEndTimestamp = Date.parse(end_timestamp) / 1000;
      if (isNaN(localStartTimestamp) || isNaN(localEndTimestamp)) {
        showError('时间格式错误');
        setLoading(false);
        return;
      }
      if (isAdminUser) {
        url = `/api/log/?p=${startIdx}&type=${logType}&username=${username}&token_name=${token_name}&model_name=${model_name}&start_timestamp=${localStartTimestamp}&end_timestamp=${localEndTimestamp}&channel=${channel}`;
      } else {
        url = `/api/log/self/?p=${startIdx}&type=${logType}&token_name=${token_name}&model_name=${model_name}&start_timestamp=${localStartTimestamp}&end_timestamp=${localEndTimestamp}`;
      }
      const res = await API.get(url);
      const { success, message, data } = res.data;
      if (success) {
        if (startIdx === 0) {
          setLogs(data);
        } else {
          setLogs((prevLogs) => {
            const newLogs = [...prevLogs];
            newLogs.splice(startIdx * ITEMS_PER_PAGE, data.length, ...data);
            return newLogs;
          });
        }
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '加载日志失败');
    } finally {
      setLoading(false);
    }
  }, [isAdminUser, logType, username, token_name, model_name, start_timestamp, end_timestamp, channel]);

  /**
   * 分页变化处理
   */
  const onPaginationChange = useCallback((e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(logs.length / ITEMS_PER_PAGE) + 1) {
        await loadLogs(activePage - 1);
      }
      setActivePage(activePage);
    })();
  }, [logs.length, loadLogs]);

  /**
   * 刷新数据
   */
  const refresh = useCallback(async () => {
    setLoading(true);
    setActivePage(1);
    await loadLogs(0);
  }, [loadLogs]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logType]);

  /**
   * 搜索日志
   */
  const searchLogs = useCallback(async () => {
    try {
      if (searchKeyword === '') {
        await loadLogs(0);
        setActivePage(1);
        return;
      }
      setSearching(true);
      const res = await API.get(`/api/log/self/search?keyword=${searchKeyword}`);
      const { success, message, data } = res.data;
      if (success) {
        setLogs(data);
        setActivePage(1);
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '搜索失败');
    } finally {
      setSearching(false);
    }
  }, [searchKeyword, loadLogs]);

  /**
   * 关键词变化处理
   */
  const handleKeywordChange = useCallback((e, { value }) => {
    setSearchKeyword(value.trim());
  }, []);

  /**
   * 排序日志
   * @param {string} key - 排序字段
   */
  const sortLog = useCallback((key) => {
    setLogs((prevLogs) => {
      if (prevLogs.length === 0) return prevLogs;
      const sortedLogs = [...prevLogs];
      if (typeof sortedLogs[0][key] === 'string') {
        sortedLogs.sort((a, b) => ('' + a[key]).localeCompare(b[key]));
      } else {
        sortedLogs.sort((a, b) => {
          if (a[key] === b[key]) return 0;
          return a[key] > b[key] ? -1 : 1;
        });
      }
      if (sortedLogs[0].id === prevLogs[0].id) {
        sortedLogs.reverse();
      }
      return sortedLogs;
    });
  }, []);

  return (
    <div className='studio-logs-panel studio-logs-usage'>
      <header className='studio-logs-section-head'>
        <div className='studio-logs-section-head__copy'>
          <span className='studio-logs-eyebrow'>USAGE LEDGER</span>
          <h2>{t('log.usage_details')}</h2>
          <p>{t('log.studio.usage_subtitle')}</p>
        </div>
        <div className='studio-logs-stat-grid'>
          <div className='studio-logs-stat-card studio-logs-stat-card--quota'>
            <span className='studio-logs-stat-card__icon'><Icon name='database' /></span>
            <div>
              <span className='studio-logs-stat-card__label'>{t('log.total_quota')}</span>
              {showStat ? (
                <strong>{renderQuota(stat.quota, t)}</strong>
              ) : (
                <button type='button' onClick={handleEyeClick} className='studio-logs-stat-reveal'>
                  <Icon name='eye' /> {t('log.click_to_view')}
                </button>
              )}
            </div>
          </div>
          <div className='studio-logs-stat-card studio-logs-stat-card--token'>
            <span className='studio-logs-stat-card__icon'><Icon name='cube' /></span>
            <div>
              <span className='studio-logs-stat-card__label'>Tokens</span>
              <strong>{showStat ? (stat.token || 0).toLocaleString() : '••••••'}</strong>
            </div>
          </div>
          <div className='studio-logs-stat-card'>
            <span className='studio-logs-stat-card__icon'><Icon name='list alternate outline' /></span>
            <div>
              <span className='studio-logs-stat-card__label'>{t('log.studio.loaded_records')}</span>
              <strong>{logs.length.toLocaleString()}</strong>
            </div>
          </div>
        </div>
      </header>

      {/* 搜索过滤区域 */}
      <div className='studio-logs-toolbar'>
        <div className='studio-logs-toolbar__heading'>
          <div>
            <span className='studio-logs-eyebrow'>QUERY BUILDER</span>
            <h3>{t('log.studio.filter_title')}</h3>
          </div>
          <Icon name='sliders horizontal' />
        </div>
        <Form>
          <Form.Group widths='equal' style={{ marginBottom: 12 }}>
            <Form.Input
              label={t('log.table.token_name')}
              placeholder={t('log.table.token_name_placeholder')}
              name='token_name'
              value={token_name}
              onChange={handleInputChange}
              size='small'
              style={{ borderRadius: 8 }}
            />
            <Form.Input
              label={t('log.table.model_name')}
              placeholder={t('log.table.model_name_placeholder')}
              name='model_name'
              value={model_name}
              onChange={handleInputChange}
              size='small'
              style={{ borderRadius: 8 }}
            />
            <Form.Input
              label={t('log.table.start_time')}
              type='datetime-local'
              name='start_timestamp'
              value={start_timestamp}
              onChange={handleInputChange}
              size='small'
              style={{ borderRadius: 8 }}
            />
            <Form.Input
              label={t('log.table.end_time')}
              type='datetime-local'
              name='end_timestamp'
              value={end_timestamp}
              onChange={handleInputChange}
              size='small'
              style={{ borderRadius: 8 }}
            />
          </Form.Group>
          {isAdminUser && (
            <Form.Group widths='equal' style={{ marginBottom: 12 }}>
              <Form.Input
                label={t('log.table.channel_id')}
                placeholder={t('log.table.channel_id_placeholder')}
                name='channel'
                value={channel}
                onChange={handleInputChange}
                size='small'
                style={{ borderRadius: 8 }}
              />
              <Form.Input
                label={t('log.table.username')}
                placeholder={t('log.table.username_placeholder')}
                name='username'
                value={username}
                onChange={handleInputChange}
                size='small'
                style={{ borderRadius: 8 }}
              />
              <Form.Input
                label={t('log.search')}
                placeholder='搜索请求内容/模型/Token...'
                value={searchKeyword}
                onChange={handleKeywordChange}
                size='small'
                icon='search'
                iconPosition='left'
                style={{ borderRadius: 8 }}
                action={
                  <Button onClick={searchLogs} loading={searching} size='small'>
                    <Icon name='search' />
                  </Button>
                }
              />
            </Form.Group>
          )}
          {!isAdminUser && (
            <Form.Input
              label={t('log.search')}
              placeholder='搜索请求内容/模型/Token...'
              value={searchKeyword}
              onChange={handleKeywordChange}
              size='small'
              icon='search'
              iconPosition='left'
              style={{ borderRadius: 8, marginBottom: 12 }}
              action={
                <Button onClick={searchLogs} loading={searching} size='small'>
                  <Icon name='search' />
                </Button>
              }
            />
          )}
          <div className='studio-logs-toolbar__actions'>
            <Button primary onClick={refresh} loading={loading}>
              <Icon name='search' /> {t('log.buttons.submit')}
            </Button>
          </div>
        </Form>
      </div>

      {/* 日志表格 */}
      <section className='studio-logs-table-card'>
        <div className='studio-logs-table-card__head'>
          <div>
            <span className='studio-logs-eyebrow'>REQUEST ACTIVITY</span>
            <h3>{t('log.studio.activity_title')}</h3>
          </div>
          <span className='studio-logs-record-count'>{t('log.studio.loaded_count', { count: logs.length })}</span>
        </div>
        <div className='studio-logs-table-scroll'>
      <Table basic='very' compact size='small' className='studio-logs-table'>
        <Table.Header>
          <Table.Row style={{ background: '#f5f5f5' }}>
            <Table.HeaderCell
              sorted={undefined}
              style={{ cursor: 'pointer', fontWeight: 600, color: '#374151' }}
              onClick={() => sortLog('created_time')}
            >
              {t('log.table.time')}
            </Table.HeaderCell>
            {isAdminUser && (
              <Table.HeaderCell
                style={{ cursor: 'pointer', fontWeight: 600, color: '#374151' }}
                onClick={() => sortLog('channel')}
              >
                {t('log.table.channel')}
              </Table.HeaderCell>
            )}
            <Table.HeaderCell
              style={{ cursor: 'pointer', fontWeight: 600, color: '#374151' }}
              onClick={() => sortLog('type')}
            >
              {t('log.table.type')}
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer', fontWeight: 600, color: '#374151' }}
              onClick={() => sortLog('model_name')}
            >
              {t('log.table.model')}
            </Table.HeaderCell>
            {showUserTokenQuota() && (
              <>
                {isAdminUser && (
                  <Table.HeaderCell
                    style={{ cursor: 'pointer', fontWeight: 600, color: '#374151' }}
                    onClick={() => sortLog('username')}
                  >
                    {t('log.table.username')}
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell
                  style={{ cursor: 'pointer', fontWeight: 600, color: '#374151' }}
                  onClick={() => sortLog('token_name')}
                >
                  {t('log.table.token_name')}
                </Table.HeaderCell>
                <Table.HeaderCell
                  textAlign='right'
                  style={{ cursor: 'pointer', fontWeight: 600, color: '#374151' }}
                  onClick={() => sortLog('prompt_tokens')}
                >
                  Prompt
                </Table.HeaderCell>
                <Table.HeaderCell
                  textAlign='right'
                  style={{ cursor: 'pointer', fontWeight: 600, color: '#374151' }}
                  onClick={() => sortLog('completion_tokens')}
                >
                  Completion
                </Table.HeaderCell>
                <Table.HeaderCell
                  style={{ cursor: 'pointer', fontWeight: 600, color: '#374151' }}
                  onClick={() => sortLog('quota')}
                >
                  {t('log.table.quota')}
                </Table.HeaderCell>
              </>
            )}
            <Table.HeaderCell style={{ fontWeight: 600, color: '#374151' }}>{t('log.table.detail')}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {logs
            .slice((activePage - 1) * ITEMS_PER_PAGE, activePage * ITEMS_PER_PAGE)
            .map((log, idx) => {
              if (log.deleted) return null;
              return (
                <Table.Row
                  key={log.id}
                  className={hoveredRow === idx ? 'is-hovered' : ''}
                  onMouseEnter={() => setHoveredRow(idx)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <Table.Cell>
                    {renderTimestamp(log.created_at, log.request_id)}
                  </Table.Cell>
                  {isAdminUser && (
                    <Table.Cell>
                      {log.source_type === 'pool' ? (
                        <Popup
                          content={log.pool_account_id ? `#${log.pool_account_id}` : ''}
                          trigger={renderColorLabel(log.pool_account_name || t('log.source.pool'))}
                        />
                      ) : log.channel_name ? (
                        <Popup
                          content={log.channel ? `#${log.channel}` : ''}
                          trigger={renderColorLabel(log.channel_name)}
                        />
                      ) : log.channel ? (
                        <span style={{
                          display: 'inline-block',
                          padding: '3px 8px',
                          borderRadius: 6,
                          background: 'rgba(0,0,0,0.08)',
                          color: '#18181b',
                          fontSize: 12,
                          fontWeight: 500,
                          border: '1px solid rgba(0,0,0,0.16)',
                        }}>#{log.channel}</span>
                      ) : (
                        <span style={{ color: '#374151' }}>-</span>
                      )}
                    </Table.Cell>
                  )}
                  <Table.Cell>{renderType(log.type)}</Table.Cell>
                  <Table.Cell>
                    {log.model_name ? renderColorLabel(log.model_name) : <span style={{ color: '#374151' }}>-</span>}
                    {log.actual_model_name && log.actual_model_name !== log.model_name && (
                      <div style={{ marginTop: 2 }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          borderRadius: 4,
                          background: '#fff7ed',
                          color: '#c2410c',
                          fontSize: 11,
                          fontWeight: 500,
                          border: '1px solid #fed7aa',
                        }}>→ {log.actual_model_name}</span>
                      </div>
                    )}
                    {/* 条件必须转成布尔：proxy_subscription_id 为 0 时，
                        数字 0 && JSX 会让 React 直接渲染出 "0" 文本 */}
                    {Boolean(log.proxy_used || log.proxy_fallback || log.proxy_subscription_id) && (
                      <div style={{ marginTop: 2 }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          borderRadius: 4,
                          background: log.proxy_fallback ? '#fff7ed' : '#eff6ff',
                          color: log.proxy_fallback ? '#c2410c' : '#1d4ed8',
                          fontSize: 11,
                          fontWeight: 500,
                          border: `1px solid ${log.proxy_fallback ? '#fed7aa' : '#bfdbfe'}`,
                        }}>
                          {log.proxy_subscription_name || `#${log.proxy_subscription_id || '-'}`}
                          {log.proxy_node_name && ` / ${log.proxy_node_name}`}
                          {log.proxy_fallback ? ` (${t('log.proxy.fallback')})` : ''}
                        </span>
                      </div>
                    )}
                  </Table.Cell>
                  {showUserTokenQuota() && (
                    <>
                      {isAdminUser && (
                        <Table.Cell>
                          {log.username ? (
                            <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{log.username}</span>
                          ) : (
                            <span style={{ color: '#374151' }}>-</span>
                          )}
                        </Table.Cell>
                      )}
                      <Table.Cell>
                        {log.token_name ? renderColorLabel(log.token_name) : <span style={{ color: '#374151' }}>-</span>}
                      </Table.Cell>
                      <Table.Cell textAlign='right' style={{ fontVariantNumeric: 'tabular-nums', color: '#374151' }}>
                        {log.prompt_tokens || <span style={{ color: '#374151' }}>-</span>}
                      </Table.Cell>
                      <Table.Cell textAlign='right' style={{ fontVariantNumeric: 'tabular-nums', color: '#374151' }}>
                        {log.completion_tokens || <span style={{ color: '#374151' }}>-</span>}
                      </Table.Cell>
                      <Table.Cell style={{ color: '#18181b', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {log.quota ? renderQuota(log.quota, t, 6) : <span style={{ color: '#374151' }}>-</span>}
                      </Table.Cell>
                    </>
                  )}
                  <Table.Cell>{renderDetail(log)}</Table.Cell>
                </Table.Row>
              );
            })}
          {logs.length === 0 && !loading && (
            <Table.Row>
              <Table.Cell colSpan={10} textAlign='center'>
                <div className='studio-logs-state'>
                  <span className='studio-logs-state__icon'><Icon name='inbox' /></span>
                  <strong>{t('log.studio.empty_title')}</strong>
                  <span>{t('log.studio.empty_description')}</span>
                </div>
              </Table.Cell>
            </Table.Row>
          )}
          {loading && logs.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={10} textAlign='center'>
                <div className='studio-logs-state studio-logs-state--loading'>
                  <span className='studio-logs-state__icon'><Icon name='spinner' loading /></span>
                  <strong>{t('log.studio.loading_title')}</strong>
                  <span>{t('log.studio.loading_description')}</span>
                </div>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>

        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan={10} style={{ background: '#f5f5f5', borderBottom: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 12, columnGap: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <Select
                    placeholder={t('log.type.select')}
                    options={LOG_OPTIONS}
                    name='logType'
                    value={logType}
                    onChange={(e, { name, value }) => setLogType(value)}
                    size='small'
                    style={{ minWidth: 120, borderRadius: 8 }}
                  />
                  <Button size='small' onClick={refresh} loading={loading}>
                    <Icon name='refresh' /> {t('log.buttons.refresh')}
                  </Button>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                  <Pagination
                    activePage={activePage}
                    onPageChange={onPaginationChange}
                    size='small'
                    siblingRange={1}
                    totalPages={Math.max(1, Math.ceil(logs.length / ITEMS_PER_PAGE))}
                  />
                </div>
              </div>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
        </div>
      </section>
    </div>
  );
};

export default LogsTable;
